<template>
  <div class="font-message-input" :data-e2e="FormCypressAttribute.inputs.MESSAGE_INPUT">
    <section class="editor-container">
      <div
        :id="editorId"
        ref="editable"
        class="editor-div form-text p-3 has-border-light is-radius-small is-relative"
        :placeholder="placeholder"
      />
    </section>
    <transition name="fade">
      <p
        v-if="isCreatingList"
        class="hint-text is-size-7 m-l-1 has-text-grey-dark is-absolute has-line-height-1"
      >
        {{ $t('inbox.enter-to-return-new-list-item') }}
      </p>
    </transition>
  </div>
</template>

<script>
import 'tributejs/tribute.css';
import 'quill/dist/quill.snow.css';
import { randomString } from '@/modules/core/app/helpers/StringHelper';
import { FormComputedValueMixin } from '/@/modules/core/form/mixins/FormComputedValueMixin';
import { FormCypressAttribute } from '@/modules/core/form/constants/FormCypressAttribute';
import Lang from '/@/modules/core/app/services/Lang';
import { RichTextEditorRenderer } from '@/modules/core/form/renderers/RichTextEditorRenderer';
import { TributeConfig } from '@/modules/core/form/models/TributeConfig';
import KeyboardAdapter from '@/modules/core/ui/services/KeyboardService';
import { KeyboardEvent, KeyCode } from '@/modules/core/ui/ui.constants';
import DeviceService from '@/modules/core/ui/services/DeviceService';
import { HasContainerMixin } from '@/modules/core/app/mixins/HasContainerMixin';
import { markRaw } from 'vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'FormMessageInput',
  emits: ['update:mentions', 'on-enter'],
  mixins: [FormComputedValueMixin, HasContainerMixin],
  props: {
    mentionables: {
      type: Array,
      default: () => []
    },
    trigger: {
      type: String,
      default: '@'
    },
    mentionOptionComponent: {
      type: Object
    },
    lookupField: { type: String, default: 'name' },
    placeholder: { type: String, default: () => Lang.t('estatetask.start-typing') },
    canSubmit: {
      type: Boolean,
      default: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    showEmoji: {
      type: Boolean,
      default: true
    },
    showLink: {
      type: Boolean,
      default: true
    },
    showToolbar: {
      type: Boolean,
      default: true
    }
  },
  static: {
    FormCypressAttribute
  },
  local() {
    return {
      editURLButtonText: `"${Lang.t('asset.edit')}"`,
      removeURLButtonText: `"${Lang.t('estateaccount.remove')}"`,
      newURLButtonText: `"${Lang.t('messages.new-url')}"`,
      saveURLButtonText: `"${Lang.t('core.save')}"`
    };
  },
  data() {
    return {
      mentions: [],
      renderer: null,
      editorId: randomString(5),
      isCreatingList: false,
      cursorFromBottom: '35px',
      toolbarFromBottom: '80px'
    };
  },
  computed: {
    keyboardEvent() {
      return DeviceService.isMac() ? KeyboardEvent.CMD_ENTER : KeyboardEvent.ENTER;
    }
  },
  methods: {
    onInput(delta, oldDelta, source) {
      if (source === 'user') {
        this.isCreatingList = this.renderer.isCreatingList();
        this.cursorFromBottom = this.renderer.getCursorFromBottom();
        this.toolbarFromBottom = this.renderer.getToolbarFromBottom();
      }

      setTimeout(() => {
        this.computedValue = this.renderer.getText();
      }, 0);
    },
    focus() {
      this.$el.querySelector('.ql-editor')?.focus();
    },
    onMention(event) {
      const item = event.detail.item;
      const context = event.detail.context;
      this.mentions.push(item.original);
      this.$emit('update:mentions', this.mentions);

      const text = `${this.trigger}${item.original.contact.fullName}`;
      const triggerLength = context?.mentionTriggerChar?.length ?? this.trigger.length;
      const mentionTextLength = context?.mentionText?.length ?? 0;

      this.renderer.insertMention(text, triggerLength, !!mentionTextLength);
    },
    onShowMentions() {
      document.body.style.position = 'relative';
    },
    onHideMentions() {
      document.body.style.position = 'unset';
    },
    init() {
      this.renderer = markRaw(this.$resolve(RichTextEditorRenderer, this.editorId));
      this.renderer.addQuill(
        this.onInput,
        this.computedValue,
        this.placeholder,
        this.showEmoji,
        this.showLink,
        this.showToolbar
      );
      this.renderer.addTribute(
        new TributeConfig({
          element: this.$el.querySelector('.ql-editor'),
          mentionables: this.mentionables,
          trigger: this.trigger,
          mentionOptionComponent: this.mentionOptionComponent,
          mentionableLookupField: this.lookupField,
          onMention: this.onMention,
          onShowMentions: this.onShowMentions,
          onHideMentions: this.onHideMentions
        })
      );
    },
    onEnter(event) {
      if (this.skipSubmit(event)) {
        return;
      }

      this.$nextTick(() => this.$emit('on-enter'));
    },
    skipSubmit(event) {
      if (!this.canSubmit) {
        return true;
      }

      const isEnterPressed = event && event.keyCode === KeyCode.ENTER.value;

      if (DeviceService.isMac()) {
        return isEnterPressed && !event.metaKey;
      }

      return isEnterPressed && !event.ctrlKey;
    }
  },
  mounted() {
    this.init();
  },
  created() {
    KeyboardAdapter.on(this.keyboardEvent, this.onEnter);
  },
  beforeUnmount() {
    KeyboardAdapter.off(this.keyboardEvent, this.onEnter);
  },
  watch: {
    readOnly(value) {
      this.renderer.toggleDisabled(value);
    }
  }
};
</script>

<style scoped>
.hint-text {
  bottom: var(--ce--12px);
}
.form-text {
  &:focus-visible {
    outline: var(--ce-primary-light-20) auto var(--ce-1px);
  }

  &:empty:before {
    content: attr(placeholder);
    color: var(--ce-grey);
    pointer-events: none;
    display: block;
  }
}

:deep(.ql-snow .ql-tooltip a.ql-action::after) {
  content: v-bind(editURLButtonText);
  font-size: var(--ce-14px);
}

:deep(.ql-snow .ql-tooltip a.ql-remove::before) {
  content: v-bind(removeURLButtonText);
  font-size: var(--ce-14px);
}

:deep(.emoji_completions) {
  bottom: v-bind(cursorFromBottom) !important;
}

:deep(#emoji-palette) {
  bottom: v-bind(toolbarFromBottom) !important;
}

:deep(.ql-tooltip[data-mode='link']::before) {
  content: v-bind(newURLButtonText);
}

:deep(.ql-tooltip[data-mode='link'] a.ql-action::after) {
  content: v-bind(saveURLButtonText);
}
</style>
